import { Component, computed, inject, input, output, signal, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ItemType, LocaleValue, LocalizedString, PriceUnit } from '@hjc/masterdata-types';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { orderBy } from 'lodash';
import { DialogComponent } from '@services/dialog/dialog-component';
import { LocalizationService } from '@services/localization.service';
import { NotificationService } from '@services/notification.service';
import { SharedTranslationModule } from '@shared/translation.module';

export type AddPriceItem = {
  id: string;
  name: LocalizedString;
  type: ItemType;
};

type PriceMeasure = {
  id: string;
  name: string;
};

export type AddPriceData<T extends AddPriceItem = AddPriceItem> = {
  items: T[];
  measure: PriceMeasure[];
  editMode?: boolean;
  initialData?: Partial<AddPriceResult>;
};

export type AddPriceResult = {
  itemId: string;
  itemName: LocalizedString;
  itemType: ItemType;
  price: number;
  unit: PriceUnit;
  validFrom: Date;
  setLoading?: (isLoading: boolean) => void;
  setErrorMessage?: (message: string | null) => void;
};

type AddPriceFormValue = {
  itemId: string;
  price: number | null;
  unit: string;
  validFrom: Date;
};

@Component({
  selector: 'md-add-price',
  imports: [
    AsyncPipe,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatDatepickerModule,
    MatButtonModule,
    MatProgressSpinner,
    ReactiveFormsModule,
    MatIconModule,
    TranslateModule,
    SharedTranslationModule
  ],
  templateUrl: './add-price.component.html',
  styleUrl: './add-price.component.scss',
  standalone: true
})
export class AddPriceComponent<T extends AddPriceItem = AddPriceItem> implements DialogComponent<AddPriceData<T>, AddPriceResult>, OnChanges {
  private notificationService = inject(NotificationService);
  private localizationService = inject(LocalizationService);
  private translate = inject(TranslateService);
  private fb = inject(FormBuilder);

  data = input<AddPriceData<T> | undefined>(undefined);
  dialogClose = output<void>();
  dialogSubmit = output<AddPriceResult>();

  readonly lang = signal<LocaleValue>(this.localizationService.currentLang);
  readonly isLoading = signal<boolean>(true);
  readonly isEditMode = signal<boolean>(false);
  readonly items = signal<T[]>([]);
  readonly measures = signal<PriceMeasure[]>([]);
  readonly serverErrorMessage = signal<string | null>(null);
  readonly isSubmitting = signal<boolean>(false);

  form: FormGroup<{
    itemId: FormControl<string>;
    price: FormControl<number | null>;
    unit: FormControl<string>;
    validFrom: FormControl<Date>;
  }> = this.fb.group({
    itemId: this.fb.control<string>('', { validators: [Validators.required, this.itemValidator.bind(this)], nonNullable: true }),
    price: this.fb.control<number | null>(null, { validators: [Validators.required, Validators.min(0)] }),
    unit: this.fb.control<string>('', { validators: [Validators.required], nonNullable: true }),
    validFrom: this.fb.control<Date>(this.stripTimeFromDate(new Date()), { validators: [Validators.required], nonNullable: true })
  });

  readonly filteredItems: Observable<T[]> = this.form.controls.itemId.valueChanges.pipe(
    startWith(''),
    map(value => this.filterItems(value || ''))
  );

  readonly dialogTitle = computed(() => (this.isEditMode() ? this.translate.instant('ADD_PRICE.TITLE_EDIT') : this.translate.instant('ADD_PRICE.TITLE')));
  readonly saveButtonText = computed(() => this.translate.instant('COMMON.ACTIONS.SAVE'));
  readonly selectedItem = computed(() => {
    const itemId = this.form.controls.itemId.value;
    return this.items().find(item => item.id === itemId);
  });
  readonly maxDate = computed(() => (this.isEditMode() ? null : this.stripTimeFromDate(new Date())));

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data()) {
      this.processData(this.data()!);
    }
  }

  displayItemFn = (itemId: string): string => {
    if (!itemId) return '';
    const item = this.items().find(i => i.id === itemId);
    return item ? item.name[this.lang()] : itemId;
  };

  private itemValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    const item = this.items().find(i => i.id === value);
    return item ? null : { invalidItem: true };
  }

  private filterItems(value: string): T[] {
    let filteredItems: T[];

    if (!value) {
      filteredItems = this.items();
    } else {
      const isItemId = this.items().some(item => item.id === value);
      if (isItemId) {
        filteredItems = this.items();
      } else {
        const filterValue = value.toLowerCase();
        filteredItems = this.items().filter(item => item.name[this.lang()]?.toLowerCase().includes(filterValue));
      }
    }

    return orderBy(filteredItems, [item => item.name[this.lang()]?.toLowerCase() || ''], ['asc']);
  }

  private stripTimeFromDate(date: Date): Date {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate;
  }

  private processData(data: AddPriceData<T>): void {
    this.items.set(data.items || []);
    this.measures.set(data.measure || []);
    this.isEditMode.set(!!data.editMode);

    if (this.isEditMode() && data.initialData) {
      const validFrom = data.initialData.validFrom ? this.stripTimeFromDate(data.initialData.validFrom) : this.stripTimeFromDate(new Date());
      this.form.setValue({
        itemId: data.initialData.itemId || '',
        price: data.initialData.price ?? null,
        unit: data.initialData.unit || '',
        validFrom
      });
    } else if (data.measure?.length > 0 && !this.form.controls.unit.value) {
      this.form.controls.unit.setValue(data.measure[0].id);
    }
    this.isLoading.set(false);
  }

  cancel(): void {
    this.dialogClose.emit();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.serverErrorMessage.set(null);
    const formValue = this.form.getRawValue() as AddPriceFormValue;
    const selectedTarget = this.items().find(i => i.id === formValue.itemId);
    if (!selectedTarget) {
      this.notificationService.error(this.translate.instant('ADD_PRICE.ERROR.SELECTED_ITEM_NOT_FOUND'));
      return;
    }
    this.dialogSubmit.emit({
      itemId: selectedTarget.id,
      itemName: selectedTarget.name,
      itemType: selectedTarget.type,
      price: formValue.price as number,
      unit: formValue.unit as PriceUnit,
      validFrom: formValue.validFrom,
      setLoading: (isLoading: boolean) => this.isSubmitting.set(isLoading),
      setErrorMessage: (message: string | null) => this.serverErrorMessage.set(message)
    });
  }
}
